// services/postProductionService.ts
import axiosInstance from '../utils/axiosConfig'; // Import your custom axios instance
import { 
  PostProductionData, 
  PostProductionResponse, 
  PostProductionListResponse,
  CreatePostProductionRequest,
  UpdatePostProductionRequest
} from '../types/PostProduction';

/**
 * Fetch all post-production reports
 * @param status Optional filter by status
 * @param search Optional search term
 * @param page Page number
 * @param limit Items per page
 */
export const getPostProductionReports = async (
  status?: 'draft' | 'completed',
  search?: string,
  page: number = 1,
  limit: number = 10
): Promise<PostProductionListResponse> => {
  try {
    // Build query params
    const params = new URLSearchParams();
    if (status) params.append('status', status);
    if (search) params.append('search', search);
    params.append('page', page.toString());
    params.append('limit', limit.toString());
    
    const response = await axiosInstance.get(`/api/post-production?${params.toString()}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching post-production reports:', error);
    return {
      success: false,
      error: 'Failed to fetch post-production reports'
    };
  }
};

/**
 * Fetch a specific post-production report by ID
 * @param id Post-production report ID
 */
export const getPostProductionById = async (id: string): Promise<PostProductionResponse> => {
  try {
    const response = await axiosInstance.get(`/api/post-production/${id}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching post-production report:', error);
    return {
      success: false,
      error: 'Failed to fetch post-production report'
    };
  }
};

/**
 * Fetch post-production report by performance ID
 * @param performanceId Performance ID
 */
export const getPostProductionByPerformance = async (performanceId: string): Promise<PostProductionResponse> => {
  try {
    const response = await axiosInstance.get(`/api/post-production/performance/${performanceId}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching post-production by performance:', error);
    // If the error is 404, it means no report exists yet
    if (typeof error === 'object' && error !== null && 'response' in error && (error as any).response?.status === 404) {
      return {
        success: false,
        error: 'No post-production report found for this performance'
      };
    }
    return {
      success: false,
      error: 'Failed to fetch post-production report'
    };
  }
};

/**
 * Create a new post-production report
 * @param data CreatePostProductionRequest data
 */
export const createPostProduction = async (
  data: CreatePostProductionRequest
): Promise<PostProductionResponse> => {
  try {
    const response = await axiosInstance.post(`/api/post-production`, data);
    return response.data;
  } catch (error) {
    console.error('Error creating post-production report:', error);
    return {
      success: false,
      error: 'Failed to create post-production report'
    };
  }
};

/**
 * Update an existing post-production report
 * @param id Post-production report ID
 * @param updates Data to update
 */
export const updatePostProduction = async (
  id: string,
  updates: Partial<PostProductionData>
): Promise<PostProductionResponse> => {
  try {
    const response = await axiosInstance.put(`/api/post-production/${id}`, updates);
    return response.data;
  } catch (error) {
    console.error('Error updating post-production report:', error);
    return {
      success: false,
      error: 'Failed to update post-production report'
    };
  }
};

/**
 * Update report status (draft/completed)
 * @param id Post-production report ID
 * @param status New status
 */
export const updatePostProductionStatus = async (
  id: string,
  status: 'draft' | 'completed'
): Promise<PostProductionResponse> => {
  try {
    const response = await axiosInstance.patch(`/api/post-production/${id}/status`, { status });
    return response.data;
  } catch (error) {
    console.error('Error updating post-production status:', error);
    return {
      success: false,
      error: 'Failed to update post-production status'
    };
  }
};

/**
 * Delete a post-production report (admin only)
 * @param id Post-production report ID
 */
export const deletePostProduction = async (
  id: string
): Promise<{ success: boolean; message?: string; error?: string }> => {
  try {
    const response = await axiosInstance.delete(`/api/post-production/${id}`);
    return response.data;
  } catch (error) {
    console.error('Error deleting post-production report:', error);
    return {
      success: false,
      error: 'Failed to delete post-production report'
    };
  }
};